// src/features/training/TrainingGrid.jsx
import React, { useMemo, useState } from "react";
import { Box, Card, CardContent, Typography, Chip, CircularProgress, Stack, Tooltip, Divider, InputBase } from "@mui/material";
import { Search, X, Clock, Star, Users, ArrowRight, IndianRupee } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Link as RouterLink } from "react-router-dom";
import { motion } from "framer-motion";
import { getPublicTrainings } from "../../api/trainings";
import { fadeUp } from "../../utils/animations";
import { MODE_FILTERS, FilterSidebar, TrainingThumb, formatPrice } from "./trainingShared";
import { useNavigate } from "react-router-dom";

// Same card height as TrainingSection's TrainingCard, so both surfaces
// present identical proportions.
const CARD_HEIGHT = 470;
const CARD_MEDIA_HEIGHT = 190;
const SIDEBAR_WIDTH = 240;

// Must match Header.jsx's TOOLBAR_HEIGHT — the fixed navbar's height.
// Sticky elements on this page dock just below it.
const NAVBAR_HEIGHT = 84;
const STICKY_TOP = NAVBAR_HEIGHT + 16; // small breathing room under the navbar
const STICKY_ZINDEX = 5; // below MUI AppBar's default (1100), above cards

// Exact copy of TrainingSection's EnrollButton — full-width gradient CTA,
// not the small inline text link this grid used before.
function EnrollButton({ label, to }) {
  return (
    <Box
      component={RouterLink}
      to={to}
      onClick={(e) => e.stopPropagation()}
      className="training-enroll-btn"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 1,
        width: "100%",
        py: 1.35,
        borderRadius: "12px",
        fontWeight: 700,
        fontSize: 14.5,
        color: "white",
        textDecoration: "none",
        background: "linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)",
        boxShadow: "0 10px 22px -10px rgba(37,99,235,0.6)",
        transition: "transform 0.22s ease, box-shadow 0.22s ease, background 0.22s ease",
        "&:hover": {
          transform: "translateY(-1px)",
          boxShadow: "0 14px 28px -10px rgba(37,99,235,0.75)",
          background: "linear-gradient(135deg, #2E6EF0 0%, #2050E0 100%)",
        },
      }}
    >
      {label}
      <ArrowRight size={16} style={{ transition: "transform 0.2s ease" }} />
    </Box>
  );
}

function TrainingGridCard({ t }) {
  const navigate = useNavigate();
  const priceLabel = formatPrice(t.price, t.currency);
  const enrollHref = `/enroll?training=${encodeURIComponent(t.slug || "")}&title=${encodeURIComponent(t.title || "")}`;
const detailHref = `/public/trainings/slug/${t.slug}`;

  const handleCardClick = () => {
    if (t.slug) navigate(detailHref);
  };


  return (
   <Card
      onClick={handleCardClick}
      className="training-grid-card"
      sx={{
        height: CARD_HEIGHT,
        width: "100%",
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        textDecoration: "none",
        color: "inherit",
        border: "1px solid rgba(15,23,42,0.09)",
        borderRadius: "12px",
        boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
        cursor: t.slug ? "pointer" : "default",
        transition: "transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease",
        "&:hover": {
          transform: "translateY(-3px)",
          boxShadow: "0 16px 32px -20px rgba(15,23,42,0.22)",
          borderColor: "rgba(15,23,42,0.13)",
        },
        "&:hover .training-thumb-img": { transform: "scale(1.04)" },
        "&:hover .training-title": { color: "#2563EB" },
      }}
    >
      <TrainingThumb src={t.imageUrl} mode={t.mode} isFeatured={t.featured} height={CARD_MEDIA_HEIGHT} />

      <CardContent
        sx={{
          flex: 1,
          display: "grid",
          gridTemplateRows: "30px 44px 40px 1fr auto",
          rowGap: 0,
          pl: 2.25,
          pr: 2.5,
          pt: 2.25,
          pb: 2.5,
          minHeight: 0,
          minWidth: 0,
          overflow: "hidden",
          "&:last-child": { pb: 2.5 },
        }}
      >
        {/* Row 1: badges */}
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ overflow: "visible" }}>
          {t.badge && (
            <Chip
              label={t.badge}
              size="small"
              sx={{ height: 20, fontSize: 11, fontWeight: 700, bgcolor: "#111827", color: "white" }}
            />
          )}
          {t.mode && (
            <Chip
              label={MODE_FILTERS.find((m) => m.value === t.mode)?.label || t.mode}
              size="small"
              sx={{ height: 20, fontSize: 11, fontWeight: 700, bgcolor: "rgba(37,99,235,0.08)", color: "#2563EB" }}
            />
          )}
          {t.level && (
            <Chip
              label={t.level}
              size="small"
              sx={{ height: 20, fontSize: 11, fontWeight: 700, bgcolor: "rgba(15,23,42,0.05)", color: "#334155" }}
            />
          )}
        </Stack>

        {/* Row 2: title */}
        <Box sx={{ display: "flex", alignItems: "center", overflow: "hidden" }}>
          <Tooltip title={t.title} arrow placement="top" enterDelay={400}>
            <Typography
              className="training-title"
              sx={{
                fontWeight: 700,
                fontSize: "1.05rem",
                color: "#0B1530",
                lineHeight: "22px",
                transition: "color 0.2s ease",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                width: "100%",
                wordBreak: "break-word",
              }}
            >
              {t.title}
            </Typography>
          </Tooltip>
        </Box>

        {/* Row 3: tagline */}
        <Box sx={{ overflow: "hidden" }}>
          <Typography
            sx={{
              fontSize: 13.5,
              color: "#64748B",
              lineHeight: "20px",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              textOverflow: "ellipsis",
              width: "100%",
              wordBreak: "break-word",
            }}
          >
            {t.shortDescription}
          </Typography>
        </Box>

        {/* Row 4: flexible spacer */}
        <Box />

        {/* Row 5: footer — matches TrainingSection's TrainingCard footer exactly */}
        <Box>
          <Divider sx={{ mb: 1.5 }} />
          <Stack direction="row" spacing={2.5} sx={{ mb: 1.5, flexWrap: "wrap" }}>
            {t.duration && (
              <Stack direction="row" spacing={1.25} alignItems="center">
                <Box sx={{ width: 32, height: 32, borderRadius: "50%", bgcolor: "#EFF6FF", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <Clock size={15} color="#2563EB" />
                </Box>
                <Stack spacing={0}>
                  <Typography sx={{ fontSize: 10.5, color: "#94A3B8", fontWeight: 600, lineHeight: 1.3 }}>Duration</Typography>
                  <Typography sx={{ fontSize: 13, color: "#0B1530", fontWeight: 700, lineHeight: 1.3 }}>{t.duration}</Typography>
                </Stack>
              </Stack>
            )}
            {priceLabel && (
              <Stack direction="row" spacing={1.25} alignItems="center">
                <Box sx={{ width: 32, height: 32, borderRadius: "50%", bgcolor: "#EFF6FF", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <IndianRupee size={15} color="#2563EB" />
                </Box>
                <Stack spacing={0}>
                  <Typography sx={{ fontSize: 10.5, color: "#94A3B8", fontWeight: 600, lineHeight: 1.3 }}>Fee</Typography>
                  <Typography sx={{ fontSize: 13, color: "#0B1530", fontWeight: 700, lineHeight: 1.3 }}>{priceLabel}</Typography>
                </Stack>
              </Stack>
            )}
          </Stack>

          {(t.rating != null || t.enrollmentCount != null) && (
            <Stack direction="row" spacing={1.5} sx={{ mb: 1.25 }}>
              {t.rating != null && (
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Star size={13} color="#F59E0B" />
                  <Typography sx={{ fontSize: 12, color: "#64748B", fontWeight: 600 }}>{t.rating}</Typography>
                </Stack>
              )}
              {t.enrollmentCount != null && (
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Users size={13} color="#94A3B8" />
                  <Typography sx={{ fontSize: 12, color: "#64748B", fontWeight: 600 }}>{t.enrollmentCount}+</Typography>
                </Stack>
              )}
            </Stack>
          )}

          <EnrollButton label={t.ctaLabel || "Enroll now"} to={enrollHref} />
        </Box>
      </CardContent>
      
    </Card>
  );
}

// Search input + mode chips, used as-is in the sticky top bar.
function SearchAndModeBar({ search, setSearch, mode, setMode, focused, setFocused }) {
  return (
    <Box
      sx={{
        borderRadius: "16px",
        border: "1px solid rgba(15,23,42,0.07)",
        bgcolor: "white",
        boxShadow: "0 4px 16px -6px rgba(15,23,42,0.12)",
        p: { xs: 2, sm: 2.75 },
      }}
    >
      <Box
        sx={{
          display: "flex", alignItems: "center", gap: 1.25,
          bgcolor: "#F8FAFF", borderRadius: "11px", border: "1px solid",
          borderColor: focused ? "#2563EB" : "rgba(15,23,42,0.08)",
          boxShadow: focused ? "0 0 0 4px rgba(37,99,235,0.10)" : "none",
          px: 2, py: 1.2, mb: 2,
          transition: "border-color 0.15s ease, box-shadow 0.15s ease",
        }}
      >
        <Search size={18} color={focused ? "#2563EB" : "#94A3B8"} style={{ flexShrink: 0 }} />
        <InputBase
          fullWidth
          placeholder="Search trainings"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          sx={{ fontSize: 14.5, fontWeight: 500 }}
        />
        {search.length > 0 && (
          <Box
            role="button"
            aria-label="Clear search"
            onClick={() => setSearch("")}
            sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: 20, height: 20, borderRadius: "50%", color: "#94A3B8", cursor: "pointer" }}
          >
            <X size={13} />
          </Box>
        )}
      </Box>

      <Stack direction="row" flexWrap="wrap" gap={1}>
        {MODE_FILTERS.map((m) => {
          const isActive = mode === m.value;
          return (
            <Chip
              key={m.value}
              label={m.label}
              onClick={() => setMode(m.value)}
              sx={{
                fontWeight: 700, fontSize: 13, borderRadius: "999px", cursor: "pointer",
                bgcolor: isActive ? "#2563EB" : "rgba(37,99,235,0.06)",
                color: isActive ? "white" : "#2563EB",
                "&:hover": { bgcolor: isActive ? "#1D4ED8" : "rgba(37,99,235,0.12)" },
              }}
            />
          );
        })}
      </Stack>
    </Box>
  );
}

export default function TrainingGrid({ category, heading = "All training programs" }) {
  const [search, setSearch] = useState("");
  const [mode, setMode] = useState("");
  const [level, setLevel] = useState("");
  const [focused, setFocused] = useState(false);

  // The backend (see /api/public/trainings/) only accepts `mode`,
  // `featured`, `page`, and `pageSize` as query params — it does NOT
  // filter by category, search text, or level, and has no sort/order
  // params. So we only pass `mode` to the API (the one thing it can
  // actually filter on) and request the max pageSize, then do category,
  // search, level filtering and displayOrder sorting client-side below.
  const { data, isLoading, isError } = useQuery({
    queryKey: ["public/trainings-all", mode],
    queryFn: () =>
      getPublicTrainings({
        ...(mode && { mode }),
        pageSize: 100,
      }).then((r) => r.data?.data ?? r.data),
  });

  // Trainings scoped to this page's category (mode already applied
  // server-side), before level/search are applied — this is the pool
  // the level sidebar's options and the results grid are both drawn
  // from, so counts and results always agree.
  const categoryScoped = useMemo(() => {
    let list = Array.isArray(data) ? data : data?.results || [];
    if (category) {
      list = list.filter((t) => t.category === category);
    }
    return list;
  }, [data, category]);

  // Level options are NOT a static list — they're built from whatever
  // `level` values actually show up on the trainings themselves (e.g.
  // "Microsoft", "CISCO", "INTERMEDIATE", "Sun Solaris" all coexist in
  // the sample data), so the sidebar always matches the real data.
  const levelOptions = useMemo(() => {
    const counts = new Map();
    categoryScoped.forEach((t) => {
      const lvl = (t.level || "").trim();
      if (!lvl) return;
      counts.set(lvl, (counts.get(lvl) || 0) + 1);
    });
    const opts = Array.from(counts.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([lvl, count]) => ({ label: `${lvl} (${count})`, value: lvl }));
    return [{ label: `All Category (${categoryScoped.length})`, value: "" }, ...opts];
  }, [categoryScoped]);

  const trainings = useMemo(() => {
    let list = categoryScoped;

    if (level) {
      list = list.filter((t) => (t.level || "").trim() === level);
    }

    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(
        (t) =>
          (t.title || "").toLowerCase().includes(q) ||
          (t.shortDescription || "").toLowerCase().includes(q) ||
          (t.category || "").toLowerCase().includes(q)
      );
    }

    // Client-side sort since the API doesn't support sort/order.
    return [...list].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
  }, [categoryScoped, level, search]);

  // If the selected level no longer exists in this scope (e.g. mode
  // changed and that level has no trainings left), fall back to "All".
  const activeLevel = levelOptions.some((o) => o.value === level) ? level : "";

  return (
    <Box component="section" sx={{ bgcolor: "#FAFBFF", py: { xs: 6, md: 8 } }}>
      <Box sx={{ maxWidth: 1280, mx: "auto", px: { xs: 2, md: 4 } }}>
        <Typography sx={{ fontWeight: 800, fontSize: { xs: "1.6rem", md: "2rem" }, color: "#0B1530", mb: 3 }}>
          {heading}
        </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: `${SIDEBAR_WIDTH}px 1fr` },
            gap: { xs: 3, md: 4 },
            alignItems: "start",
          }}
        >
          {/* Left column: level filter — sticky under the navbar on md+ */}
          <Box
            sx={{
              position: { xs: "static", md: "sticky" },
              top: STICKY_TOP,
              zIndex: STICKY_ZINDEX,
              maxHeight: { md: `calc(100vh - ${STICKY_TOP + 24}px)` },
              overflowY: { md: "auto" },
              borderRadius: "16px",
              border: "1px solid rgba(15,23,42,0.07)",
              bgcolor: "white",
              boxShadow: "0 1px 3px rgba(15,23,42,0.05)",
              p: 2,
            }}
          >
            <FilterSidebar title="Category" options={levelOptions} value={activeLevel} onChange={setLevel} />
          </Box>

          {/* Right column: search bar, mode chips, results grid */}
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ mb: 4 }}>
              <SearchAndModeBar
                search={search}
                setSearch={setSearch}
                mode={mode}
                setMode={setMode}
                focused={focused}
                setFocused={setFocused}
              />
            </Box>

            {isLoading ? (
              <Box display="flex" justifyContent="center" py={8}><CircularProgress size={26} thickness={4} sx={{ color: "#2563EB" }} /></Box>
            ) : isError ? (
              <Box textAlign="center" py={8}>
                <Typography fontWeight={600} color="#B91C1C">Couldn't load trainings. Please try again.</Typography>
              </Box>
            ) : trainings.length === 0 ? (
              <Box textAlign="center" py={8}>
                <Typography fontWeight={600} color="#334155">No trainings found</Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, 1fr)",
                    lg: "repeat(3, 1fr)",
                  },
                  gap: 3,
                }}
              >
                {trainings.map((t, i) => (
                  <motion.div key={t.id} {...fadeUp(i * 0.03)}>
                    <TrainingGridCard t={t} />
                  </motion.div>
                ))}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}